<template>
	<template v-if="showAdminShell">
		<div class="admin-wrapper">
			<dev-breakpoint-indicator></dev-breakpoint-indicator>
			<div id="page">
				<layout-admin-sidebar></layout-admin-sidebar>
				<div id="content">
					<RouterView/>
				</div>
			</div>
		</div>
	</template>
	<template v-else>
		<RouterView/>
	</template>
	<cfl-flyout-backdrop></cfl-flyout-backdrop>
</template>

<style>
	@reference "@main-css";
	@import "@shared-css/toast.css";

	.admin-wrapper {
		@apply w-screen h-svh overflow-clip flex flex-row;
		@apply bg-(--color-body-bg) dark:bg-(--color-body-bg-alt);
		@apply text-(--color-body-text) dark:text-(--color-body-text-alt);

		#page {
			@apply basis-full w-screen flex relative overflow-clip;
			@apply flex-col-reverse;
			@apply md:flex-row;

			> #content {
				@apply flex flex-col grow overflow-hidden min-w-0;
			}
		}
	}
</style>

<script setup lang="ts">
	import { computed, onMounted } from "vue";
	import useAccessibilityStore from "@shared-stores/accessibility.store";
	import useAuthStore from "@shared-stores/auth.store";
	import { RouterView, useRoute } from "vue-router";
	import CflFlyoutBackdrop from "@shared-components/cfl/cfl-flyout-backdrop.vue";
	import LayoutAdminSidebar from "@/components/layout/layout-admin-sidebar.vue";
	import DevBreakpointIndicator from "@shared-components/system/dev-breakpoint-indicator.vue";
	import { getClaimedLostItemsCount, getDashboardStats, getUnreadMessagesCount } from "@/services/dashboard.service";
	import useAdminStatsStore from "@/store/admin.stats.store";
	import SharedPubSub from "@shared/shared.pub.sub";
	import PubSubTopicEnum from "@shared-enums/communication/pub.sub.topic.enum";
	import PubSubEventEnum from "@shared-enums/communication/pub.sub.event.enum";
	import PubSubSessionActionEnum from "@shared-enums/communication/pub.sub.session.action.enum";
	import PubSubEventRegistrationActionEnum from "@shared-enums/communication/pub.sub.event.registration.action.enum";
	import useToastInfo from "@shared-composables/useToastInfo";
	import { getUnreadSurveysUserCount } from "@shared-services/box/survey.service";
	import { getUsersSubscriptionsCounts } from "@/services/shop/user.subscription.service";
	import { DateTime } from "luxon";

	const accessibilityStore = useAccessibilityStore();
	const authStore = useAuthStore();
	const route = useRoute();

	/** Shell admin (sidebar incluse) : uniquement si connecté ET pas sur la page de login. Évite que
	 *  `/login` (rendu par redirection quand le token est périmé mais isLoggedIn encore vrai) s'affiche
	 *  DANS le layout authentifié → form de connexion + sidebar visibles en même temps. */
	const showAdminShell = computed<boolean>(() => authStore.isLoggedIn && route.path !== "/login");
	const adminStatsStore = useAdminStatsStore();

	const updateUserSubscription = async (payload: { data: any; source?: string }) =>
	{
		const stats = await getUsersSubscriptionsCounts();
		adminStatsStore.userSubscriptionsCount = stats["status"]["all"];

		const text = payload.data?.message ?? "Un client s'est abonné.";
		useToastInfo(text);
	};

	const updateContactMessage = async () =>
	{
		adminStatsStore.unreadMessagesCount = await getUnreadMessagesCount();
		useToastInfo("Un nouveau message a été envoyé depuis le formulaire de contact.");
	};

	const updateSurvey = async () =>
	{
		adminStatsStore.unreadSurveyUsersCount = await getUnreadSurveysUserCount();
		useToastInfo("Une nouvelle personne a répondu à un sondage.");
	};

	const updateLostItem = async () =>
	{
		adminStatsStore.claimedLostItemsCount = await getClaimedLostItemsCount();
		useToastInfo("Les affaires oubliées ont reçu une nouvelle mise à jour.");
	};

	const updateUserRegister = async () =>
	{
		adminStatsStore.usersCount++;
		useToastInfo("Un nouveau membre vient de s'inscrire.");
	};

	const updateSessionUpdate = async (payload: { data: any; source?: string }) =>
	{
		const action = payload.data?.action ?? "registered";
		const userName = payload.data?.userName ?? "Un adhérent";
		const sessionLabel = payload.data?.sessionLabel ?? "une séance";
		const dayIso = payload.data?.dayIso;
		const dayLabel = dayIso
			? DateTime.fromISO(dayIso).setLocale("fr").toFormat("cccc dd MMMM")
			: "";
		const sessionFull = dayLabel ? `${ sessionLabel } (${ dayLabel })` : sessionLabel;

		// Validation présence par l'admin/coach : action déclenchée par l'admin lui-même → pas de toast
		// (les écrans concernés se rafraîchissent silencieusement via PubSub).
		if (action === PubSubSessionActionEnum.ATTENDANCE_UPDATED || action === PubSubSessionActionEnum.NOTE_FOR_COACH_UPDATED)
		{
			return;
		}

		switch (action)
		{
			case PubSubSessionActionEnum.REGISTERED_ON_WAITLIST:
				useToastInfo(`${ userName } s'est inscrit sur liste d'attente de ${ sessionFull }.`);
				break;
			case PubSubSessionActionEnum.REGISTERED_ON_RESERVED_SPOT:
				useToastInfo(`${ userName } a reçu une place réservée pour ${ sessionFull } (en attente de confirmation).`);
				break;
			case PubSubSessionActionEnum.REGISTERED_FROM_WAITLIST:
				useToastInfo(`${ userName } a été inscrit automatiquement depuis la liste d'attente pour ${ sessionFull }.`);
				break;
			case PubSubSessionActionEnum.REGISTERED_FROM_CAPACITY_INCREASE:
				useToastInfo(`${ userName } a été inscrit automatiquement à ${ sessionFull } (capacité augmentée).`);
				break;
			case PubSubSessionActionEnum.UNREGISTERED:
				useToastInfo(`${ userName } s'est désinscrit de ${ sessionFull }.`);
				break;
			case PubSubSessionActionEnum.UNREGISTERED_LATE:
				useToastInfo(`${ userName } s'est désinscrit tardivement de ${ sessionFull }.`);
				break;
			case PubSubSessionActionEnum.UNREGISTERED_FROM_WAITLIST:
				useToastInfo(`${ userName } s'est désinscrit de la liste d'attente de ${ sessionFull }.`);
				break;
			case PubSubSessionActionEnum.UNREGISTERED_FROM_RESERVED_SPOT:
				useToastInfo(`${ userName } a refusé sa place réservée pour ${ sessionFull }.`);
				break;
			case PubSubSessionActionEnum.UNREGISTERED_RESERVED_SPOT_EXPIRED:
				useToastInfo(`${ userName } n'a pas confirmé sa place réservée à temps pour ${ sessionFull }.`);
				break;
			default:
				useToastInfo(`${ userName } s'est inscrit à ${ sessionFull }.`);
				break;
		}
	};

	const updateEventRegistration = async (payload: { data: any }) =>
	{
		// Annulation technique (réservation remplacée ou abandonnée) : les écrans se relisent, rien à annoncer.
		if (! payload.data?.action)
		{
			return;
		}

		const userName = payload.data?.userName ?? "Un adhérent";
		const eventTitle = payload.data?.eventTitle ?? "un événement";

		switch (payload.data?.action)
		{
			case PubSubEventRegistrationActionEnum.UNREGISTERED:
				useToastInfo(`${ userName } s'est désinscrit(e) de ${ eventTitle }.`);
				break;
			case PubSubEventRegistrationActionEnum.REFUNDED:
				useToastInfo(`${ userName } a été remboursé(e) pour ${ eventTitle } : sa place est libérée.`);
				break;
			default:
				useToastInfo(`${ userName } s'est inscrit(e) à ${ eventTitle }.`);
				break;
		}
	};

	onMounted(async () =>
	{
		await authStore.init();
		accessibilityStore.init();

		// Ne charger les stats et les subscriptions PubSub que si l'utilisateur est connecté
		if (! authStore.isLoggedIn)
		{
			return;
		}

		const stats = await getDashboardStats();
		adminStatsStore.usersCount = stats["users"];
		adminStatsStore.userSubscriptionsCount = stats["user_subscriptions"];
		adminStatsStore.unreadMessagesCount = stats["unread_messages"];
		adminStatsStore.unreadSurveyUsersCount = stats["unread_surveys"];
		adminStatsStore.claimedLostItemsCount = stats["claimed_lost_items"];
		adminStatsStore.pendingAttendeesCount = stats["pending_attendees"];

		SharedPubSub.on(PubSubEventEnum.ADMIN_USER_SUBSCRIPTION_UPDATE, updateUserSubscription);
		SharedPubSub.on(PubSubEventEnum.ADMIN_CONTACT_MESSAGE_UPDATE, updateContactMessage);
		SharedPubSub.on(PubSubEventEnum.ADMIN_SURVEY_UPDATE, updateSurvey);
		SharedPubSub.on(PubSubEventEnum.ADMIN_LOST_ITEM_UPDATE, updateLostItem);
		SharedPubSub.on(PubSubEventEnum.ADMIN_SESSION_UPDATE, updateSessionUpdate);
		SharedPubSub.on(PubSubEventEnum.ADMIN_USER_REGISTER, updateUserRegister);
		SharedPubSub.on(PubSubEventEnum.ADMIN_EVENT_REGISTRATION_UPDATE, updateEventRegistration);
		SharedPubSub.connect([ PubSubTopicEnum.ADMIN ]);
	});
</script>
